import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Button,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  List,
  ListItem,
  ListItemText,
  Divider,
} from "@mui/material";
import { AddLocationAlt, Edit, Delete } from "@mui/icons-material";
import { MapContainer, TileLayer, Marker, Polyline, useMapEvents, useMap } from "react-leaflet";
import L from "leaflet";
import { useEffect, useState } from "react";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

const ClickHandler = ({ onMapClick }) => {
  useMapEvents({
    click(e) {
      onMapClick([e.latlng.lat, e.latlng.lng]);
    },
  });
  return null;
};

const FitBounds = ({ path }) => {
  const map = useMap();

  useEffect(() => {
    if (path.length > 1) {
      const bounds = L.latLngBounds(path);
      map.fitBounds(bounds, { padding: [30, 30] });
    } else if (path.length === 1) {
      map.setView(path[0], 15);
    }
  }, [path, map]);

  return null;
};

const AddLatLongDialog = ({ open, onClose, video, onSave }) => {
  const [clickedCoord, setClickedCoord] = useState(null);
  const [newCoords, setNewCoords] = useState([]);
  const [mode, setMode] = useState("add");
  const [editableCoords, setEditableCoords] = useState([]);
  const [pendingEdits, setPendingEdits] = useState([]);
  const [pendingDeletes, setPendingDeletes] = useState([]);
  const [appliedEdits, setAppliedEdits] = useState([]);
  const [deletedCoords, setDeletedCoords] = useState([]);
  const [showReview, setShowReview] = useState(false);
  const [editedMap, setEditedMap] = useState({});

  const existingPolyline = Array.isArray(video?.coordinates)
    ? video.coordinates.map((coord) => [parseFloat(coord.lat), parseFloat(coord.lng)])
    : [];

  const fallbackCenter = [20.5937, 78.9629];

  useEffect(() => {
    setEditableCoords(existingPolyline);
  }, [video]);

  useEffect(() => {
    if (open) {
      setNewCoords([]);
      setPendingEdits([]);
      setAppliedEdits([]);
      setPendingDeletes([]);
      setDeletedCoords([]);
      setClickedCoord(null);
      setMode("add");
    }
  }, [open]);

  const handleMapClick = (coord) => {
    if (mode === "add") {
      setClickedCoord(coord);
    }
  };

  const handleAdd = () => {
    if (clickedCoord) {
      setNewCoords((prev) => [...prev, clickedCoord]);
      setClickedCoord(null);
    }
  };

  const handleApplyEdits = () => {
    const updated = editableCoords.map((coord, idx) => pendingEdits[idx] || coord);
    const confirmed = pendingEdits
      .map((coord, idx) => (coord ? coord : null))
      .filter((c) => c !== null);
    setEditableCoords(updated);
    setAppliedEdits((prev) => [...prev, ...confirmed]);
    setPendingEdits([]);
  };

  const handleDeleteSelected = () => {
    const toDelete = editableCoords.filter((_, i) => pendingDeletes.includes(i));
    setDeletedCoords((prev) => [...prev, ...toDelete]);
    const remaining = editableCoords.filter((_, i) => !pendingDeletes.includes(i));
    setEditableCoords(remaining);
    setPendingDeletes([]);
  };

  const handleSave = () => {
    const updatedCoords = editableCoords.map((coord, idx) =>
      pendingEdits[idx] ? pendingEdits[idx] : coord
    );
  
    const finalCoords = updatedCoords.filter((_, idx) => !pendingDeletes.includes(idx));
  
    if (
      finalCoords.length === 0 &&
      newCoords.length === 0 &&
      appliedEdits.length === 0 &&
      deletedCoords.length === 0
    ) {
      alert("No changes to save.");
      return;
    }
    const edited = Object.entries(editedMap).map(([idx, { original, updated }]) => ({
      original: { lat: original[0], lng: original[1] },
      updated: { lat: updated[0], lng: updated[1] },
    }));
    
    const payload = {
      added: newCoords.map(([lat, lng]) => ({ lat, lng })),
      edited,
      deleted: deletedCoords.map(([lat, lng]) => ({ lat, lng }))
    };
  
    console.log("Structured Payload to Pass to Parent:", payload);
  
    onSave(payload);
    onClose();
  };
  
  

  const isSaveDisabled = newCoords.length === 0 && appliedEdits.length === 0 && deletedCoords.length === 0;

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Add Coordinates for {video.filename}</DialogTitle>
      <DialogContent style={{ height: 900 }}>
        <ToggleButtonGroup
          value={mode}
          exclusive
          onChange={(_, newMode) => newMode && setMode(newMode)}
          style={{ marginBottom: 12 }}
        >
          <ToggleButton value="add">
            <AddLocationAlt fontSize="small" /> Add
          </ToggleButton>
          <ToggleButton value="edit">
            <Edit fontSize="small" /> Edit
          </ToggleButton>
          <ToggleButton value="delete">
            <Delete fontSize="small" /> Delete
          </ToggleButton>
        </ToggleButtonGroup>

        <MapContainer
          center={existingPolyline[0] || fallbackCenter}
          zoom={13}
          style={{ height: "70%", width: "100%", marginBottom: 16 }}
        >
          <TileLayer
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            attribution="&copy; OpenStreetMap contributors"
          />
          <ClickHandler onMapClick={handleMapClick} />
          <FitBounds path={editableCoords} />

          {editableCoords.length >= 2 && (
            <Polyline
              positions={editableCoords}
              pathOptions={{
                color: "#006400",
                weight: 6,
                opacity: 0.9,
              }}
            />
          )}

          {editableCoords.map((coord, idx) => (
            <Marker
              key={`editable-${idx}`}
              position={coord}
              draggable={mode === "edit"}
              eventHandlers={{
                dragend: (e) => {
                  if (mode === "edit") {
                    const newLatLng = e.target.getLatLng();
                    setEditedMap((prev) => ({
                      ...prev,
                      [idx]: {
                        original: editableCoords[idx],     // store what it was
                        updated: [newLatLng.lat, newLatLng.lng],  // store what it became
                      },
                    }));
                    setEditableCoords((prev) => {
                      const updated = [...prev];
                      updated[idx] = [newLatLng.lat, newLatLng.lng];
                      return updated;
                    });
                    setPendingEdits((prev) => {
                      const updated = [...prev];
                      updated[idx] = [newLatLng.lat, newLatLng.lng];
                      return updated;
                    });
                  }
                },
                click: () => {
                  if (mode === "delete") {
                    setPendingDeletes((prev) => [...prev, idx]);
                  }
                },
              }}
            />
          ))}

          {newCoords.map((coord, index) => (
            <Marker key={`new-${index}`} position={coord} />
          ))}

          {clickedCoord && <Marker position={clickedCoord} />}
        </MapContainer>

        {clickedCoord && (
          <div style={{ marginTop: 8 }}>
            <Typography variant="subtitle2">Selected Lat-Long:</Typography>
            <Typography variant="body2">
              Latitude: {clickedCoord[0].toFixed(6)} | Longitude: {clickedCoord[1].toFixed(6)}
            </Typography>
            <Button onClick={handleAdd} variant="outlined" size="small" style={{ marginTop: 8 }}>
              Add Lat-Long
            </Button>
          </div>
        )}

        {mode === "edit" && pendingEdits.some((e) => e) && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <Typography variant="subtitle2">Pending Edits:</Typography>
            <List dense>
              {pendingEdits.map((coord, idx) =>
                coord ? (
                  <ListItem key={`pe-${idx}`}>
                    <ListItemText
                      primary={`Lat: ${coord[0].toFixed(6)}, Lng: ${coord[1].toFixed(6)}`}
                    />
                  </ListItem>
                ) : null
              )}
            </List>
            <Button onClick={handleApplyEdits} variant="outlined" size="small">
              Apply Edits
            </Button>
          </>
        )}

        {mode === "delete" && pendingDeletes.length > 0 && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <Typography variant="subtitle2">Pending Deletes:</Typography>
            <List dense>
              {pendingDeletes.map((idx) => {
                const coord = editableCoords[idx];
                return (
                  <ListItem key={`pd-${idx}`}>
                    <ListItemText
                      primary={`Lat: ${coord[0].toFixed(6)}, Lng: ${coord[1].toFixed(6)}`}
                    />
                  </ListItem>
                );
              })}
            </List>
            <Button onClick={handleDeleteSelected} variant="outlined" size="small">
              Delete Selected
            </Button>
          </>
        )}

        {newCoords.length > 0 && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <Typography variant="subtitle2">Added Coordinates:</Typography>
            <List dense>
              {newCoords.map(([lat, lng], idx) => (
                <ListItem key={idx}>
                  <ListItemText primary={`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`} />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {appliedEdits.length > 0 && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <Typography variant="subtitle2">Edited Coordinates:</Typography>
            <List dense>
              {appliedEdits.map(([lat, lng], idx) => (
                <ListItem key={`edited-${idx}`}>
                  <ListItemText primary={`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`} />
                </ListItem>
              ))}
            </List>
          </>
        )}

        {deletedCoords.length > 0 && (
          <>
            <Divider style={{ margin: "16px 0" }} />
            <Typography variant="subtitle2">Deleted Coordinates:</Typography>
            <List dense>
              {deletedCoords.map(([lat, lng], idx) => (
                <ListItem key={`deleted-${idx}`}>
                  <ListItemText primary={`Lat: ${lat.toFixed(6)}, Lng: ${lng.toFixed(6)}`} />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained" disabled={isSaveDisabled}>
          Save Lat Long to Video
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AddLatLongDialog;
